import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AppCommonModule } from '@app/components/app.common.module';
import { ModalService } from '@app/components/modal/modal.service';
import { NotificationService } from '@app/services/notifications.service';
import {
  CreateRegionDrmPayload,
  RegionDrmCatalogService,
  UpdateRegionDrmPayload,
} from '../region-drm-catalog.service';

@Component({
  selector: 'app-region-drm-form-modal',
  standalone: true,
  imports: [AppCommonModule, CommonModule, ReactiveFormsModule],
  templateUrl: './region-drm-form-modal.component.html',
  styleUrl: './region-drm-form-modal.component.scss',
})
export class RegionDrmFormModalComponent implements OnChanges {
  @Input() isEdit = false;
  @Input() catalogId: number | null = null;
  /** Incrementar en cada apertura del modal para recargar el formulario */
  @Input() openKey = 0;

  @Output() saved = new EventEmitter<void>();

  form: FormGroup;
  isLoading = false;

  segmentOptions = [
    { label: 'Pasaje', value: 'AUTOBUSES' },
    { label: 'Carga', value: 'CARGA' },
  ];

  drmUserOptions: { label: string; value: number }[] = [];
  selectedDrmOption: { label: string; value: number } | null = null;
  selectedSegmentOption: { label: string; value: string } | null = null;
  segmentSelectDefault: string | undefined = 'AUTOBUSES';
  drmSelectDefault: number | null | undefined;

  branchOptions: { id: number; name: string; description: string }[] = [];
  selectedBranchIds: number[] = [];
  regionLabel = '';

  private readonly emptyBranchIds: number[] = [];

  constructor(
    private readonly fb: FormBuilder,
    private readonly modalService: ModalService,
    private readonly catalogService: RegionDrmCatalogService,
    private readonly notification: NotificationService,
  ) {
    this.form = this.fb.group({
      segment: ['AUTOBUSES', Validators.required],
      regionNumber: [null as number | null, [Validators.required, Validators.min(1)]],
      drmUserId: [null as number | null, Validators.required],
      enabled: [true],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['openKey'] || changes['isEdit'] || changes['catalogId']) {
      this.initModal();
    }
  }

  get regionTypeLabel(): string {
    const segment = this.form.get('segment')?.value;
    return segment === 'CARGA' ? 'Zona' : 'Región';
  }

  closeModal(): void {
    this.modalService.closeModal();
  }

  getSelectedBranchValues(): number[] {
    return this.selectedBranchIds.length
      ? this.selectedBranchIds
      : this.emptyBranchIds;
  }

  onSegmentChange(value: string | null): void {
    const segment = value ?? 'AUTOBUSES';
    this.selectedSegmentOption =
      this.segmentOptions.find((o) => o.value === segment) ?? null;
    this.form.patchValue({ segment });
    if (!this.isEdit) {
      this.selectedBranchIds = [];
      this.loadBranches(segment);
    }
  }

  onDrmUserChange(userId: number | null): void {
    this.selectedDrmOption =
      this.drmUserOptions.find((o) => o.value === userId) ?? null;
    this.form.patchValue({ drmUserId: userId });
  }

  onBranchesChange(selection: Array<{ id?: number; value?: number }>): void {
    this.selectedBranchIds = (selection ?? [])
      .map((item) => item.id ?? item.value)
      .filter((id): id is number => id != null)
      .map(Number);
  }

  submit(): void {
    if (this.form.invalid || this.selectedBranchIds.length === 0) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    const raw = this.form.getRawValue();

    if (this.isEdit && this.catalogId != null) {
      const payload: UpdateRegionDrmPayload = {
        drmUserId: Number(raw.drmUserId),
        dealerBranchIds: [...this.selectedBranchIds],
        enabled: !!raw.enabled,
      };
      this.catalogService.update(this.catalogId, payload).subscribe({
        next: () => {
          this.isLoading = false;
          this.saved.emit();
          this.closeModal();
        },
        error: (err) => {
          this.isLoading = false;
          this.notification.showMessage(
            [err?.error?.message ?? 'Error al actualizar la región/zona.'],
            'error',
          );
        },
      });
      return;
    }

    const payload: CreateRegionDrmPayload = {
      segment: raw.segment,
      regionNumber: Number(raw.regionNumber),
      drmUserId: Number(raw.drmUserId),
      dealerBranchIds: [...this.selectedBranchIds],
      enabled: !!raw.enabled,
    };

    this.catalogService.create(payload).subscribe({
      next: () => {
        this.isLoading = false;
        this.saved.emit();
        this.closeModal();
      },
      error: (err) => {
        this.isLoading = false;
        this.notification.showMessage(
          [err?.error?.message ?? 'Error al crear la región/zona.'],
          'error',
        );
      },
    });
  }

  private initModal(): void {
    this.isLoading = true;
    this.loadDrmUsers().then(() => {
      if (this.isEdit && this.catalogId != null) {
        this.loadDetail(this.catalogId);
      } else {
        this.resetCreateForm();
        this.isLoading = false;
      }
    });
  }

  private loadDrmUsers(): Promise<void> {
    return new Promise((resolve) => {
      this.catalogService.listDrmUsers(true).subscribe({
        next: (res) => {
          this.drmUserOptions = (res.data ?? []).map((u) => ({
            label: u.label,
            value: u.id,
          }));
          resolve();
        },
        error: () => resolve(),
      });
    });
  }

  private loadDetail(id: number): void {
    this.catalogService.getById(id).subscribe({
      next: (res) => {
        const detail = res.data;
        this.regionLabel = detail.regionLabel;
        this.form.patchValue({
          segment: detail.segment,
          regionNumber: detail.regionNumber,
          drmUserId: detail.drmUserId,
          enabled: detail.enabled,
        });
        this.selectedSegmentOption =
          this.segmentOptions.find((o) => o.value === detail.segment) ?? null;
        this.selectedDrmOption =
          this.drmUserOptions.find((o) => o.value === detail.drmUserId) ?? null;
        this.drmSelectDefault = detail.drmUserId;
        this.selectedBranchIds = [...detail.dealerBranchIds];
        this.loadBranches(detail.segment, id);
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  private resetCreateForm(): void {
    this.regionLabel = '';
    this.selectedBranchIds = [];
    this.selectedDrmOption = null;
    this.selectedSegmentOption = this.segmentOptions[0];
    this.segmentSelectDefault = 'AUTOBUSES';
    this.drmSelectDefault = undefined;
    this.branchOptions = [];
    this.form.reset({
      segment: 'AUTOBUSES',
      regionNumber: null,
      drmUserId: null,
      enabled: true,
    });
    this.loadBranches('AUTOBUSES');
  }

  private loadBranches(segment: string, excludeCatDrmId?: number): void {
    this.catalogService.listDealerBranches(segment, excludeCatDrmId).subscribe({
      next: (res) => {
        const branches = res.data ?? [];
        const selectedSet = new Set(this.selectedBranchIds);
        this.branchOptions = branches
          .filter((b) => b.selectable || selectedSet.has(b.id))
          .map((b) => ({
            id: b.id,
            name: b.name,
            description: b.assignedRegionLabel
              ? `${b.name} (asignada: ${b.assignedRegionLabel})`
              : b.name,
          }));
      },
    });
  }
}
